import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/data/content";
import { CtaButton, goTo, handleCta } from "./primitives";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = (href) => {
    setOpen(false);
    goTo(href);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[padding,background-color,box-shadow] duration-500 ${
        scrolled ? "glass-nav border-b border-brand-navy/10 py-3 shadow-[0_8px_32px_rgba(10,25,47,0.05)]" : "py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        {/* Logo */}
        <button onClick={() => nav("#topo")} data-testid="nav-logo" className="flex items-center gap-2.5">
          <img
            src={BRAND.logo}
            alt="Transferência Academy"
            className={`transition-all duration-500 ${scrolled ? "h-9" : "h-11"}`}
          />
          <span className="flex flex-col items-start leading-none">
            <span className="font-display text-[15px] md:text-base font-semibold tracking-tight text-brand-navy">
              {BRAND.name}
            </span>
            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.32em] text-brand-goldDark">
              {BRAND.submark}
            </span>
          </span>
        </button>

        {/* Desktop links */}
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => nav(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative text-sm font-medium text-brand-navy/75 transition-colors hover:text-brand-navy"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <CtaButton
            size="sm"
            onClick={() => handleCta("checkout")}
            data-testid="nav-cta"
            className="hidden sm:inline-flex"
          >
            QUERO ME PREPARAR
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </CtaButton>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="grid h-10 w-10 place-items-center rounded-full border border-brand-navy/15 text-brand-navy lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden glass-nav border-t border-brand-navy/10 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.label}
                  onClick={() => nav(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-brand-navy hover:bg-brand-navy/5"
                >
                  {l.label}
                </button>
              ))}
              <CtaButton size="md" className="mt-2 w-full" onClick={() => handleCta("checkout")} data-testid="nav-mobile-cta">
                QUERO ME PREPARAR
              </CtaButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
